import { supabase } from "@/lib/supabase";
import { formatDateTime } from "@/lib/format";

/* =====================================================================
   Patients (linked to current professional)
   ===================================================================== */
export async function listLinkedPatients({ q, status, clinic_id, user_role } = {}) {
  const user = (await supabase.auth.getUser()).data.user;
  if (!user) return [];

  const isAdminOrStaff = user_role === 'admin' || user_role === 'receptionist';

  // 1. Fetch links
  let linkQ = supabase
    .from("patient_links")
    .select("patient_id, status, source, requested_at, approved_at, clinic_id, professional_id");
    
  if (clinic_id && isAdminOrStaff) {
    linkQ = linkQ.or(`clinic_id.eq.${clinic_id},professional_id.eq.${user.id}`);
  } else {
    linkQ = linkQ.eq("professional_id", user.id);
  }

  if (status && status !== "all") linkQ = linkQ.eq("status", status);
  const { data: allLinks, error: e1 } = await linkQ;
  if (e1) throw e1;
  if (!allLinks?.length) return [];

  // 2. Deduplicate
  const uniqueMap = new Map();
  allLinks.forEach(l => {
    const existing = uniqueMap.get(l.patient_id);
    if (!existing || (l.status === "active" && existing.status !== "active")) {
      uniqueMap.set(l.patient_id, l);
    }
  });
  const links = Array.from(uniqueMap.values());
  const ids = links.map(l => l.patient_id);

  // 3. Fetch profiles (including user_id to check if they signed up)
  let pq = supabase
    .from("profiles")
    .select(`
      id, user_id, full_name, email, phone, avatar_url, created_at, updated_at,
      patient_profile:patient_profiles!patient_profiles_profile_id_fkey ( birth_date, gender, document, address, notes, tags, risk_flags )
    `)
    .in("id", ids);
  if (q) pq = pq.ilike("full_name", `%${q}%`);
  const { data: profiles, error: e2 } = await pq;
  if (e2) throw e2;

  const byId = Object.fromEntries(profiles.map(p => [p.id, p]));
  return links
    .map(l => {
      const p = byId[l.patient_id];
      if (!p) return null;
      const pp = p.patient_profile || {};
      return {
        id: p.id,
        full_name: p.full_name,
        email: p.email,
        phone: p.phone,
        link_status: l.status,
        link_source: l.source,
        requested_at: l.requested_at,
        approved_at: l.approved_at,
        birth_date: pp.birth_date,
        gender: pp.gender,
        document: pp.document,
        address: pp.address,
        notes: pp.notes,
        tags: pp.tags || [],
        risk_flags: pp.risk_flags || [],
        updated_at: p.updated_at,
        created_at: p.created_at,
        status: l.status === "active" ? "ativo" : l.status === "pending" ? "pendente" : "inativo",
        is_ghost: !p.user_id, // If user_id is null, they haven't signed up yet
        is_pending_signup: !p.user_id
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(b.requested_at) - new Date(a.requested_at));
}

export async function getPatient(patientId, clinic_id = null) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const profUserId = user.id;
  
  // Fetch profile + patient_profile
  const { data: profile, error: e1 } = await supabase
    .from("profiles")
    .select(`
    id, user_id, full_name, email, phone, avatar_url, created_at, updated_at,
    patient_profile:patient_profiles!patient_profiles_profile_id_fkey ( birth_date, gender, document, address, notes, tags, risk_flags )
  `)
  .eq("id", patientId)
  .maybeSingle();
  
if (e1) throw e1;
if (!profile) return null;

// Fetch link status for THIS professional OR the current clinic
let lq = supabase
  .from("patient_links")
  .select("id, status, clinic_id")
  .eq("patient_id", patientId);

if (clinic_id) {
  lq = lq.or(`clinic_id.eq.${clinic_id},professional_id.eq.${profUserId}`);
} else {
  lq = lq.eq("professional_id", profUserId);
}

const { data: links, error: e2 } = await lq.limit(1);
if (e2) throw e2;
const link = links?.[0];

const pp = profile.patient_profile || {};
const s = link?.status || "inativo";

return {
  id: profile.id,
  user_id: profile.user_id,
  full_name: profile.full_name,
  email: profile.email,
  phone: profile.phone,
  created_at: profile.created_at,
  updated_at: profile.updated_at,
  birth_date: pp.birth_date,
  gender: pp.gender,
  document: pp.document,
  address: pp.address,
  notes: pp.notes,
  tags: pp.tags || [],
  risk_flags: pp.risk_flags || [],
  status: s === "active" ? "ativo" : s === "pending" ? "pendente" : "inativo",
  link_id: link?.id,
  is_pending_signup: !profile.user_id
};
}

export async function updatePatientProfile(patientId, updates) {
  // Split updates between profiles and patient_profiles tables
  const profUp = {};
  const ppUp = {};
  ["full_name", "email", "phone"].forEach(k => { if (k in updates) profUp[k] = updates[k]; });
  ["birth_date","gender","document","address","notes","tags","risk_flags"].forEach(k => {
    if (k in updates) ppUp[k] = updates[k];
  });
  if (Object.keys(profUp).length) {
    const { error } = await supabase.from("profiles").update(profUp).eq("id", patientId);
    if (error) throw error;
  }
  if (Object.keys(ppUp).length) {
    const { error } = await supabase.from("patient_profiles").update(ppUp).eq("profile_id", patientId);
    if (error) throw error;
  }
}

/* =====================================================================
   Anamnese
   ===================================================================== */
export async function getAnamnese(patientId, clinic_id = null) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const profUserId = user.id;

  const { data, error } = await supabase
    .from("anamneses")
    .select("*")
    .eq("patient_id", patientId)
    .eq("professional_id", profUserId)
    .maybeSingle();

  if (error) throw error;
  return data || { patient_id: patientId, professional_id: profUserId, clinic_id };
}

export async function listAnamneses(patientId, clinic_id = null) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const profUserId = user.id;

  let q = supabase
    .from("anamneses")
    .select(`
      *,
      professional:profiles!anamneses_professional_id_fkey ( id, full_name )
    `)
    .eq("patient_id", patientId);

  if (clinic_id) {
    q = q.or(`clinic_id.eq.${clinic_id},professional_id.eq.${profUserId}`);
  } else {
    q = q.eq("professional_id", profUserId);
  }

  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function upsertAnamnese(patientId, data, clinic_id = null) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const profUserId = user.id;
  // extra_fields should be a plain object
  const extra_fields = data.extra_fields && typeof data.extra_fields === 'object'
    ? data.extra_fields
    : {};
  const payload = { ...data, extra_fields, patient_id: patientId, professional_id: profUserId, clinic_id };
  const { data: out, error } = await supabase
    .from("anamneses")
    .upsert(payload, { onConflict: "patient_id,professional_id" })
    .select()
    .maybeSingle();
  if (error) throw error;
  return out;
}

/* =====================================================================
   Anamnese Templates (Custom Questions per Professional)
   ===================================================================== */
export async function listAnamneseTemplates() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from("anamnese_templates")
    .select("*")
    .eq("professional_id", user.id)
    .order("position", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function upsertAnamneseTemplate({ id, question_key, question_label, question_hint, section, position }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const slug = question_key || question_label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '') + '_' + Date.now();
  const payload = {
    professional_id: user.id,
    question_key: slug,
    question_label,
    question_hint: question_hint || null,
    section: section || 'custom',
    position: position ?? 0,
  };
  if (id) payload.id = id;
  const { data, error } = await supabase
    .from("anamnese_templates")
    .upsert(payload, { onConflict: "professional_id,question_key" })
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function deleteAnamneseTemplate(id) {
  const { error } = await supabase.from("anamnese_templates").delete().eq("id", id);
  if (error) throw error;
}

/* =====================================================================
   Clinical Sessions (SOAP)
   ===================================================================== */
export async function listPatientSessions(patientId) {
  const { data, error } = await supabase
    .from("clinical_sessions")
    .select(`
      *,
      professional:profiles!clinical_sessions_professional_id_fkey (
        id,
        full_name,
        professional_profile:professional_profiles!professional_profiles_profile_id_fkey ( crp )
      )
    `)
    .eq("patient_id", patientId)
    .order("session_date", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function listAllSessions(clinic_id = null) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  let q = supabase
    .from("clinical_sessions")
    .select("*, patient:profiles!clinical_sessions_patient_id_fkey ( id, full_name )")
    .order("session_date", { ascending: false })
    .limit(500);

  if (clinic_id) {
    q = q.or(`clinic_id.eq.${clinic_id},professional_id.eq.${user.id}`);
  } else {
    q = q.eq("professional_id", user.id);
  }

  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function createSession(payload, clinic_id = null) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const profUserId = user.id;
  const { data, error } = await supabase
    .from("clinical_sessions")
    .insert({ ...payload, professional_id: profUserId, clinic_id })
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function updateSession(id, payload) {
  const { data, error } = await supabase
    .from("clinical_sessions")
    .update(payload)
    .eq("id", id)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function deleteSession(id) {
  const { error } = await supabase.from("clinical_sessions").delete().eq("id", id);
  if (error) throw error;
}

/* =====================================================================
   Appointments
   ===================================================================== */
export async function listAppointments({ date_from, date_to, clinic_id, professional_id, patient_id } = {}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  let q = supabase
    .from("appointments")
    .select(`
      id, start_at, end_at, status, type, modality, notes,
      patient:profiles!appointments_patient_id_fkey ( id, full_name ),
      professional:profiles!appointments_professional_id_fkey ( id, full_name )
    `)
    .order("start_at", { ascending: true });

  // Apply filters
  if (clinic_id) {
    q = q.eq("clinic_id", clinic_id);
    if (professional_id) {
      q = q.eq("professional_id", professional_id);
    }
  } else if (professional_id) {
    q = q.eq("professional_id", professional_id);
  } else if (patient_id) {
    q = q.eq("patient_id", patient_id);
  } else {
    // Default visibility: everything involving the user
    q = q.or(`professional_id.eq.${user.id},patient_id.eq.${user.id}`);
  }

  if (date_from) q = q.gte("start_at", date_from);
  if (date_to)   q = q.lte("start_at", date_to);
  
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(a => ({ ...a, patient_name: a.patient?.full_name || "—" }));
}

export async function createAppointment(payload) {
  // 1. Create the appointment
  const { data, error } = await supabase
    .from("appointments")
    .insert(payload)
    .select()
    .maybeSingle();
    
  if (error) throw error;

  // 2. Automatic link: ensure the professional can see this patient's clinical data
  // We use ON CONFLICT DO NOTHING (implicitly by catching or using upsert with restricted fields)
  // Here we use a separate insert to avoid disturbing existing links
  if (data.patient_id && data.professional_id) {
    await supabase.from("patient_links").insert({
      patient_id: data.patient_id,
      professional_id: data.professional_id,
      clinic_id: data.clinic_id || null,
      status: 'active',
      source: 'search',
      approved_at: new Date().toISOString()
    }).select(); // We ignore errors (conflicts) here

    // 3. Consume Credit (only if professional has credit system enabled)
    const usesCredits = await getProfessionalUsesCredits(data.professional_id);
    if (usesCredits) {
      const { error: ledgerErr } = await supabase.from("credit_ledger").insert({
        patient_id: data.patient_id,
        professional_id: data.professional_id,
        clinic_id: data.clinic_id || null,
        amount: -1,
        reason: `Agendamento: ${formatDateTime(data.start_at)}`,
        expires_at: null
      });
      if (ledgerErr) {
        console.error("Erro ao consumir crédito:", ledgerErr);
      }
    }
  }

  return data;
}

export async function deleteAppointment(id) {
  // 1. Delete associated financial transaction if exists (integral removal)
  try {
    // Supabase
    await supabase
      .from("financial_transactions")
      .delete()
      .eq("appointment_id", id);
  } catch (err) {
    console.warn("Erro ao deletar transação Supabase:", err);
  }

  try {
    // LocalStorage fallback
    const mockTxs = getMockTransactions();
    const filteredMock = mockTxs.filter(t => t.appointment_id !== id);
    saveMockTransactions(filteredMock);
  } catch (err) {
    console.warn("Erro ao deletar transação LocalStorage:", err);
  }

  // 2. Delete the appointment itself
  // The database trigger 'on_appointment_deleted_refund' now handles 
  // the credit refund automatically and atomically upon deletion.
  const { error } = await supabase
    .from("appointments")
    .delete()
    .eq("id", id);

  if (error) throw error;
}

export async function updateAppointmentStatus(appointmentId, status, cancelFinancial = false) {
  // 1. Update appointment status
  const { error: apptErr } = await supabase
    .from("appointments")
    .update({ status })
    .eq("id", appointmentId);

  if (apptErr) throw apptErr;

  // 2. Handle financial transaction cancellation
  // If status is 'cancelado' (always cancel) or 'falta' (if user explicitly decided to cancel billing)
  if (status === "cancelado" || (status === "falta" && cancelFinancial)) {
    try {
      // Supabase
      await supabase
        .from("financial_transactions")
        .delete()
        .eq("appointment_id", appointmentId);
    } catch (err) {
      console.warn("Erro ao expurgar transação Supabase:", err.message);
    }

    try {
      // LocalStorage fallback
      const mockTxs = getMockTransactions();
      const filteredMock = mockTxs.filter(t => t.appointment_id !== appointmentId);
      saveMockTransactions(filteredMock);
    } catch (err) {
      console.warn("Erro ao expurgar transação LocalStorage:", err.message);
    }
  }
  // 3. Recreate the transaction if moving back to active status (agendado, confirmado, realizado)
  else if (["agendado", "confirmado", "realizado"].includes(status)) {
    try {
      // Check if transaction already exists in Supabase or Mock
      let txExists = false;

      // Check Supabase
      try {
        const { data: existingTx } = await supabase
          .from("financial_transactions")
          .select("id")
          .eq("appointment_id", appointmentId)
          .maybeSingle();
        if (existingTx) txExists = true;
      } catch (_) {}

      // Check Mock
      try {
        const mockTxs = getMockTransactions();
        if (mockTxs.some(t => t.appointment_id === appointmentId)) {
          txExists = true;
        }
      } catch (_) {}

      if (!txExists) {
        // Fetch appointment details
        const { data: appt, error: getErr } = await supabase
          .from("appointments")
          .select(`
            *,
            patient:profiles!appointments_patient_id_fkey ( id, full_name ),
            professional:profiles!appointments_professional_id_fkey ( id, full_name )
          `)
          .eq("id", appointmentId)
          .maybeSingle();

        if (!getErr && appt) {
          // Fetch commission percent
          let commissionPercent = 30.00;
          if (appt.clinic_id && appt.professional_id) {
            try {
              const { data: member } = await supabase
                .from("clinic_members")
                .select("commission_percent")
                .eq("clinic_id", appt.clinic_id)
                .eq("profile_id", appt.professional_id)
                .maybeSingle();
              if (member?.commission_percent !== undefined && member?.commission_percent !== null) {
                commissionPercent = Number(member.commission_percent);
              }
            } catch (_) {}
          }

          // Fetch session price
          let sessionPrice = 150.00;
          if (appt.professional_id) {
            try {
              const { data: profProfile } = await supabase
                .from("professional_profiles")
                .select("session_price")
                .eq("profile_id", appt.professional_id)
                .maybeSingle();
              if (profProfile?.session_price) {
                sessionPrice = Number(profProfile.session_price);
              }
            } catch (_) {}
          }

          // Fetch payment fees (defaulting to pix)
          let paymentMethod = "pix";
          let gatewayFeePercent = 0.00;
          if (appt.clinic_id) {
            try {
              const { data: clinicData } = await supabase
                .from("clinics")
                .select("payment_fees")
                .eq("id", appt.clinic_id)
                .maybeSingle();
              if (clinicData?.payment_fees?.[paymentMethod] !== undefined) {
                gatewayFeePercent = Number(clinicData.payment_fees[paymentMethod]);
              }
            } catch (_) {}
          }

          // Perform calculations
          const grossAmount = sessionPrice;
          const gatewayFeeAmount = Number((grossAmount * (gatewayFeePercent / 100)).toFixed(2));
          const netAmountAfterFees = Number((grossAmount - gatewayFeeAmount).toFixed(2));
          const clinicCommissionAmount = Number((netAmountAfterFees * (commissionPercent / 100)).toFixed(2));
          const professionalAmount = Number((netAmountAfterFees - clinicCommissionAmount).toFixed(2));

          const newTxPayload = {
            clinic_id: appt.clinic_id || "clinic-default",
            professional_id: appt.professional_id,
            professional_name: appt.professional?.full_name || "Profissional",
            patient_id: appt.patient_id,
            appointment_id: appt.id,
            gross_amount: grossAmount,
            payment_method: paymentMethod,
            gateway_fee_percent: gatewayFeePercent,
            gateway_fee_amount: gatewayFeeAmount,
            net_amount_after_fees: netAmountAfterFees,
            clinic_commission_percent: commissionPercent,
            clinic_commission_amount: clinicCommissionAmount,
            professional_amount: professionalAmount,
            competence_date: appt.start_at,
            received_at: appt.start_at,
            status: "received",
            notes: `Restaurado automaticamente - Status retornado para ${status}`
          };

          // Try Supabase insert
          try {
            const { professional_name, ...supabaseTxPayload } = newTxPayload;
            const { error: insErr } = await supabase.from("financial_transactions").insert(supabaseTxPayload);
            if (insErr) {
              console.warn("Erro ao reinserir no Supabase:", insErr.message);
            }
          } catch (supErr) {
            console.warn("Erro ao reinserir no Supabase (exceção):", supErr.message);
          }

          // Always sync to LocalStorage mock for consistency in both modes
          try {
            const mockTxs = getMockTransactions();
            const newMockTx = {
              id: "tx-" + Math.random().toString(36).substr(2, 9),
              ...newTxPayload,
              patient_name: appt.patient?.full_name || "Paciente Avulso",
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            mockTxs.unshift(newMockTx);
            saveMockTransactions(mockTxs);
          } catch (mockErr) {
            console.warn("Erro ao reinserir no LocalStorage:", mockErr.message);
          }
        }
      }
    } catch (err) {
      console.warn("Erro ao restaurar transação financeira:", err.message);
    }
  }

  return true;
}

/* =====================================================================
   Patient links / invitations
   ===================================================================== */
export async function listPendingLinks() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const profUserId = user.id;
  const { data, error } = await supabase
    .from("patient_links")
    .select(`
      id, status, source, requested_at, approved_at,
      patient:profiles!patient_links_patient_id_fkey ( id, full_name, email, phone )
    `)
    .eq("professional_id", profUserId)
    .order("requested_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function setLinkStatus(linkId, status) {
  const update = { status };
  if (status === "active") update.approved_at = new Date().toISOString();
  const { data, error } = await supabase
    .from("patient_links")
    .update(update)
    .eq("id", linkId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function listMyInvitations(clinic_id = null) {
  let q = supabase
    .from("invitations")
    .select("*")
    .order("created_at", { ascending: false });

  if (clinic_id) {
    const { data: { user } } = await supabase.auth.getUser();
    q = q.or(`clinic_id.eq.${clinic_id},professional_id.eq.${user.id}`);
  }

  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function createInvitation({ email, max_uses = 1, days_valid = 14, clinic_id = null, target_role = 'patient' }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const profUserId = user.id;
  const code = generateCode();
  const expires_at = days_valid ? new Date(Date.now() + days_valid * 86400000).toISOString() : null;
  const { data, error } = await supabase
    .from("invitations")
    .insert({ code, professional_id: profUserId, email: email || null, max_uses, expires_at, clinic_id, target_role })
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function redeemProfessionalInvite(code) {
  const { data, error } = await supabase.rpc('redeem_professional_invitation', { 
    invite_code: code.trim().toUpperCase() 
  });

  if (error) throw error;
  return data;
}

export async function deleteInvitation(id) {
  const { error } = await supabase.from("invitations").delete().eq("id", id);
  if (error) throw error;
}

export async function listClinicInvitations(clinicId) {
  const { data, error } = await supabase
    .from("invitations")
    .select("*")
    .eq("clinic_id", clinicId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function removeClinicMember(clinicId, profileId) {
  const { error } = await supabase
    .from("clinic_members")
    .delete()
    .eq("clinic_id", clinicId)
    .eq("profile_id", profileId);
  if (error) throw error;
}

export async function registerPatient({ full_name, email, phone, clinic_id, professional_id }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Não autenticado");

  const targetProfId = professional_id || user.id;
  const profileId = crypto.randomUUID();

  // 1. Create the Profile (Offline/Shadow)
  const { error: e1 } = await supabase
    .from("profiles")
    .insert({
      id: profileId,
      full_name,
      email,
      phone,
      role: "patient",
      clinic_id: clinic_id
    });
  if (e1) throw e1;

  // 2. Create Patient Profile details
  const { error: e2 } = await supabase
    .from("patient_profiles")
    .insert({
      profile_id: profileId,
      created_at: new Date().toISOString()
    });
  if (e2) {
    // Cleanup profile if patient_profile fails
    await supabase.from("profiles").delete().eq("id", profileId);
    throw e2;
  }

  // 3. Create active link with the professional
  const { error: e3 } = await supabase
    .from("patient_links")
    .insert({
      professional_id: targetProfId,
      patient_id: profileId,
      clinic_id: clinic_id,
      status: "active",
      source: "invite",
      approved_at: new Date().toISOString()
    });
  if (e3) throw e3;

  // 4. Create the invitation for the patient to claim the account later
  const code = generateCode();
  const { data: invite, error: e4 } = await supabase
    .from("invitations")
    .insert({
      code,
      professional_id: targetProfId,
      email: email || null,
      clinic_id: clinic_id || null,
      target_role: "patient",
      max_uses: 1,
      metadata: { 
        full_name, 
        phone,
        profile_id: profileId
      }
    })
    .select()
    .maybeSingle();

  if (e4) throw e4;
  return { ...invite, patient_name: full_name };
}

export async function listClinicMembers(clinicId) {
  if (!clinicId) return [];
  const { data, error } = await supabase
    .from("clinic_members")
    .select(`
      id, role, profile_id, commission_percent,
      profile:profiles ( id, full_name, email, avatar_url )
    `)
    .eq("clinic_id", clinicId);
  
  if (error) {
    // Se der erro porque a coluna commission_percent ainda não existe no banco,
    // tentamos buscar sem ela para manter o sistema rodando até o banco atualizar.
    if (error.message && error.message.includes("commission_percent")) {
      const { data: fallbackData, error: fallbackError } = await supabase
        .from("clinic_members")
        .select(`
          id, role, profile_id,
          profile:profiles ( id, full_name, email, avatar_url )
        `)
        .eq("clinic_id", clinicId);
      if (fallbackError) throw fallbackError;
      return (fallbackData || []).map(m => ({ ...m, commission_percent: 30.00 }));
    }
    throw error;
  }
  return data || [];
}

export async function updateClinicMemberCommission(clinicId, profileId, commissionPercent) {
  if (!clinicId || !profileId) return null;
  const { data, error } = await supabase
    .from("clinic_members")
    .update({ commission_percent: Number(commissionPercent) })
    .eq("clinic_id", clinicId)
    .eq("profile_id", profileId)
    .select();
  
  if (error) throw error;
  return data?.[0] || null;
}

// =====================================================================
// TAXAS DE ADQUIRENTE POR CLÍNICA (payment_fees JSONB na tabela clinics)
// Fallback resiliente: usa localStorage se a coluna ainda não existir no banco.
// Execute ADD_PAYMENT_FEES_TO_CLINICS.sql no Supabase para habilitar persistência.
// =====================================================================

const DEFAULT_PAYMENT_FEES = {
  pix: 0.0,
  credit_card: 3.0,
  debit_card: 1.5,
  health_insurance: 10.0,
  cash: 0.0,
};

function getLocalPaymentFees(clinicId) {
  try {
    const raw = localStorage.getItem(`iatros_payment_fees_${clinicId}`);
    if (raw) return { ...DEFAULT_PAYMENT_FEES, ...JSON.parse(raw) };
  } catch (_) {}
  return { ...DEFAULT_PAYMENT_FEES };
}

function saveLocalPaymentFees(clinicId, fees) {
  try {
    localStorage.setItem(`iatros_payment_fees_${clinicId}`, JSON.stringify(fees));
  } catch (_) {}
}

export async function getClinicPaymentFees(clinicId) {
  if (!clinicId) return { ...DEFAULT_PAYMENT_FEES };
  try {
    const { data, error } = await supabase
      .from("clinics")
      .select("payment_fees")
      .eq("id", clinicId)
      .maybeSingle();

    if (error && error.message.includes("payment_fees")) {
      // Coluna não existe ainda no banco — usar localStorage
      return getLocalPaymentFees(clinicId);
    }
    if (error) throw error;
    if (data?.payment_fees) {
      const merged = { ...DEFAULT_PAYMENT_FEES, ...data.payment_fees };
      saveLocalPaymentFees(clinicId, merged); // sincronizar cache local
      return merged;
    }
  } catch (err) {
    console.warn("[PaymentFees] Usando fallback local:", err.message);
  }
  return getLocalPaymentFees(clinicId);
}

export async function updateClinicPaymentFees(clinicId, fees) {
  if (!clinicId) return;
  const merged = { ...DEFAULT_PAYMENT_FEES, ...fees };
  // Sempre salva localmente primeiro (garante persistência mesmo sem a coluna)
  saveLocalPaymentFees(clinicId, merged);
  try {
    const { error } = await supabase
      .from("clinics")
      .update({ payment_fees: merged })
      .eq("id", clinicId);
    if (error && !error.message.includes("payment_fees")) throw error;
    if (error) {
      console.warn("[PaymentFees] Coluna não existe no banco, salvo localmente.");
    }
  } catch (err) {
    console.warn("[PaymentFees] Erro ao salvar no banco, mantido localmente:", err.message);
  }
  return merged;
}

export async function syncClinicAppointments(clinicId) {
  if (!clinicId) return { success: false, syncedCount: 0 };
  
  try {
    // 1. Obter membros da clínica para ter suas comissões
    const members = await listClinicMembers(clinicId);
    const commissionMap = new Map(); // profile_id -> commission_percent
    members.forEach(m => {
      const comm = m.commission_percent !== undefined && m.commission_percent !== null 
        ? Number(m.commission_percent) 
        : 30.00;
      commissionMap.set(m.profile_id, comm);
    });

    // 2. Obter todas as consultas da clínica para reconciliação
    const { data: appointments, error: apptErr } = await supabase
      .from("appointments")
      .select(`
        id, start_at, status, patient_id, professional_id,
        professional_profile:profiles!appointments_professional_id_fkey ( id, full_name )
      `)
      .eq("clinic_id", clinicId);

    if (apptErr) throw apptErr;
    if (!appointments || appointments.length === 0) {
      return { success: true, syncedCount: 0 };
    }

    // 3. Separar as faturáveis das não faturáveis (faltas/canceladas)
    const billableAppointments = appointments.filter(a => ["confirmado", "realizado", "agendado"].includes(a.status));
    const nonBillableApptIds = appointments
      .filter(a => ["falta", "cancelado"].includes(a.status))
      .map(a => a.id);

    // 4. Deletar transações financeiras vinculadas a consultas que foram canceladas ou marcadas como falta/ausência
    if (nonBillableApptIds.length > 0) {
      const { error: delErr } = await supabase
        .from("financial_transactions")
        .delete()
        .eq("clinic_id", clinicId)
        .in("appointment_id", nonBillableApptIds);
      if (delErr) {
        console.warn("Erro ao expurgar transações de consultas com faltas/cancelamentos:", delErr.message);
      }
    }

    // 5. Obter transações financeiras já existentes para evitar duplicidade
    const { data: existingTransactions, error: txErr } = await supabase
      .from("financial_transactions")
      .select("appointment_id")
      .eq("clinic_id", clinicId)
      .not("appointment_id", "is", null);

    if (txErr) throw txErr;
    const existingAppointmentIds = new Set(existingTransactions?.map(t => t.appointment_id) || []);

    // 6. Filtrar consultas faturáveis não faturadas ainda
    const unbilledAppointments = billableAppointments.filter(a => !existingAppointmentIds.has(a.id));
    if (unbilledAppointments.length === 0) {
      return { success: true, syncedCount: 0 };
    }

    // 7. Mapear e preparar novos lançamentos
    // Obter taxas configuradas da clínica (pix, credit_card, etc.)
    const clinicFees = await getClinicPaymentFees(clinicId);

    const transactionsToInsert = [];
    for (const appt of unbilledAppointments) {
      // Valor da consulta do profissional ou padrão 150.00 se null
      let price = 150.00;
      try {
        const { data: profProfile } = await supabase
          .from("professional_profiles")
          .select("session_price")
          .eq("profile_id", appt.professional_id)
          .maybeSingle();
        if (profProfile?.session_price) {
          price = Number(profProfile.session_price);
        }
      } catch (e) {
        console.warn("Erro ao buscar session_price para prof", appt.professional_id, e);
      }

      // Comissão configurada da clínica para este profissional
      const commissionPercent = commissionMap.get(appt.professional_id) ?? 30.00;

      // Taxa de adquirente: sincronização usa pix como padrão
      const paymentMethod = "pix";
      const gatewayFeePercent = clinicFees[paymentMethod] ?? 0.00;

      // Calcular valores
      const grossAmount = price;
      const gatewayFeeAmount = Number((grossAmount * (gatewayFeePercent / 100)).toFixed(2));
      const netAmountAfterFees = Number((grossAmount - gatewayFeeAmount).toFixed(2));
      const clinicCommissionAmount = Number((netAmountAfterFees * (commissionPercent / 100)).toFixed(2));
      const professionalAmount = Number((netAmountAfterFees - clinicCommissionAmount).toFixed(2));

      transactionsToInsert.push({
        clinic_id: clinicId,
        professional_id: appt.professional_id,
        professional_name: appt.professional_profile?.full_name || "Profissional",
        patient_id: appt.patient_id,
        appointment_id: appt.id,
        gross_amount: grossAmount,
        payment_method: paymentMethod,
        gateway_fee_percent: gatewayFeePercent,
        gateway_fee_amount: gatewayFeeAmount,
        net_amount_after_fees: netAmountAfterFees,
        clinic_commission_percent: commissionPercent,
        clinic_commission_amount: clinicCommissionAmount,
        professional_amount: professionalAmount,
        competence_date: appt.start_at,
        received_at: appt.start_at,
        status: "received",
        notes: "Sincronização automática de consulta histórica"
      });
    }

    // 8. Inserir novos lançamentos no Supabase
    if (transactionsToInsert.length > 0) {
      const supabaseTxToInsert = transactionsToInsert.map(({ professional_name, ...tx }) => tx);
      const { error: insertErr } = await supabase
        .from("financial_transactions")
        .insert(supabaseTxToInsert);
      
      if (insertErr) throw insertErr;
    }

    return { success: true, syncedCount: transactionsToInsert.length };
  } catch (err) {
    console.error("Erro na sincronização de consultas:", err);
    throw err;
  }
}




function generateCode() {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const r = (n) => Array.from({ length: n }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join("");
  return `${r(3)}-${r(4)}`;
}

/* =====================================================================
   Patient-facing
   ===================================================================== */
export async function listPublicProfessionals(q) {
  let query = supabase.from("public_professionals").select("*").order("full_name");
  if (q) query = query.ilike("full_name", `%${q}%`);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function redeemInviteCode(code) {
  const { data, error } = await supabase.rpc("redeem_invite_code", { p_code: code });
  if (error) throw error;
  return data;
}

export async function requestLink(professional_id) {
  const { data, error } = await supabase.rpc("request_link", { p_professional_id: professional_id });
  if (error) throw error;
  return data;
}

export async function listMyLinks() {
  const user = (await supabase.auth.getUser()).data.user;
  if (!user) return [];
  const userId = user.id;

  // Find the profile(s) for this user (both direct ID and mapped user_id)
  const { data: myProfs } = await supabase
    .from("profiles")
    .select("id")
    .or(`id.eq.${userId},user_id.eq.${userId}`);
  
  if (!myProfs || myProfs.length === 0) return [];
  const profileIds = myProfs.map(p => p.id);

  const { data, error } = await supabase
    .from("patient_links")
    .select(`
      id, status, source, requested_at, approved_at,
      professional:profiles!patient_links_professional_id_fkey ( id, full_name, email, avatar_url )
    `)
    .in("patient_id", profileIds)
    .order("requested_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

/* =====================================================================
   Dashboard
   ===================================================================== */
export async function dashboardStats(clinic_id, user_role) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const profUserId = user.id;
  const now = new Date();
  const startToday = new Date(now); startToday.setHours(0,0,0,0);
  const endToday = new Date(now);   endToday.setHours(23,59,59,999);
  
  const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - now.getDay()); startOfWeek.setHours(0,0,0,0);
  const endOfWeek = new Date(startOfWeek); endOfWeek.setDate(startOfWeek.getDate() + 7);
  
  const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const isAdminOrStaff = user_role === 'admin' || user_role === 'receptionist';

  // Tenant-aware base filters
  let linkBase = supabase.from("patient_links").select("patient_id, status, profiles!patient_links_patient_id_fkey!inner(id)");
  let apptBase = supabase.from("appointments").select("id", { count: "exact", head: true });
  let sessBase = supabase.from("clinical_sessions").select("id", { count: "exact", head: true });
  let upcomingBase = supabase.from("appointments").select("*, patient:profiles!appointments_patient_id_fkey(full_name)");
  let recentLinksBase = supabase.from("patient_links").select("patient_id, approved_at, patient:profiles!patient_links_patient_id_fkey(id, full_name)");

  if (clinic_id && isAdminOrStaff) {
    linkBase = linkBase.or(`clinic_id.eq.${clinic_id},professional_id.eq.${profUserId}`);
    apptBase = apptBase.or(`clinic_id.eq.${clinic_id},professional_id.eq.${profUserId}`);
    sessBase = sessBase.or(`clinic_id.eq.${clinic_id},professional_id.eq.${profUserId}`);
    upcomingBase = upcomingBase.or(`clinic_id.eq.${clinic_id},professional_id.eq.${profUserId}`);
    recentLinksBase = recentLinksBase.or(`clinic_id.eq.${clinic_id},professional_id.eq.${profUserId}`);
  } else {
    linkBase = linkBase.eq("professional_id", profUserId);
    apptBase = apptBase.eq("professional_id", profUserId);
    sessBase = sessBase.eq("professional_id", profUserId);
    upcomingBase = upcomingBase.eq("professional_id", profUserId);
    recentLinksBase = recentLinksBase.eq("professional_id", profUserId);
  }

  const [linksRes, todayApptsRes, weekApptsRes, monthSessRes, upcomingRes, recentLinksRes] = await Promise.all([
    linkBase,
    apptBase.gte("start_at", startToday.toISOString()).lt("start_at", endToday.toISOString()),
    supabase.from("appointments").select("id", { count: "exact", head: true })
      .or((clinic_id && isAdminOrStaff) ? `clinic_id.eq.${clinic_id},professional_id.eq.${profUserId}` : `professional_id.eq.${profUserId}`)
      .gte("start_at", startOfWeek.toISOString())
      .lt("start_at", endOfWeek.toISOString()),
    sessBase.gte("session_date", startMonth.toISOString()),
    upcomingBase.gte("start_at", now.toISOString()).order("start_at").limit(5),
    recentLinksBase.eq("status", "active").order("approved_at", { ascending: false }).limit(5),
  ]);

  const links = linksRes.data || [];
  const uniquePatientIds = new Set(links.map(l => l.patient_id));
  const activePatientIds = new Set(links.filter(l => l.status === "active").map(l => l.patient_id));
  const pendingLinksCount = links.filter(l => l.status === "pending").length;

  return {
    total_patients: uniquePatientIds.size,
    active_patients: activePatientIds.size,
    pending_links: pendingLinksCount,
    today_appointments: todayApptsRes.count || 0,
    week_appointments: weekApptsRes.count || 0,
    sessions_this_month: monthSessRes.count || 0,
    upcoming_appointments: (() => {
      const seen = new Set();
      return (upcomingRes.data || [])
        .map(a => ({ ...a, patient_name: a.patient?.full_name || "—" }))
        .filter(a => {
          if (seen.has(a.id)) return false;
          seen.add(a.id);
          return true;
        });
    })(),
    recent_patients: (() => {
      const seen = new Set();
      return (recentLinksRes.data || [])
        .map(r => r.patient)
        .filter(p => {
          if (!p || seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });
    })(),
    onboarding: {
      hasClinic: await supabase.from("clinics").select("id", { count: "exact", head: true }).eq("owner_id", profUserId).then(r => (r.count || 0) > 0),
      hasAvailability: await supabase.from("professional_availability").select("id", { count: "exact", head: true }).eq("professional_id", profUserId).then(r => (r.count || 0) > 0),
      hasProfile: await supabase.from("professional_profiles").select("bio, crp").eq("profile_id", profUserId).maybeSingle().then(r => !!(r.data?.bio && r.data?.crp))
    }
  };
}
/* =====================================================================
   Credits & Plans (Manual release)
   ===================================================================== */

export async function listPlans() {
  const { data, error } = await supabase.from("plans").select("*").order("credits_amount");
  if (error) throw error;
  return data || [];
}

export async function createPlanRequest(professionalId, planId, clinic_id = null) {
  const patientId = (await supabase.auth.getUser()).data.user.id;
  const { data, error } = await supabase
    .from("plan_requests")
    .insert({ patient_id: patientId, professional_id: professionalId, plan_id: planId, clinic_id })
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function listPlanRequests(clinic_id = null) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const profUserId = user.id;
  let q = supabase
    .from("plan_requests")
    .select(`
      *,
      plan:plans ( name, credits_amount ),
      patient:profiles!plan_requests_patient_id_fkey ( id, full_name, email )
    `);

  if (clinic_id) {
    q = q.or(`clinic_id.eq.${clinic_id},professional_id.eq.${profUserId}`);
  } else {
    q = q.eq("professional_id", profUserId);
  }

  const { data, error } = await q.order("requested_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function processPlanRequest(requestId, status) {
  const { data, error } = await supabase
    .from("plan_requests")
    .update({ status, processed_at: new Date().toISOString() })
    .eq("id", requestId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function addPatientCredits(patientId, amount, reason = "compra", validityDays = null, clinic_id = null) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Usuário não autenticado");
  
  const numAmount = Number(amount);
  if (isNaN(numAmount)) throw new Error("Quantidade de créditos inválida (NaN)");

  // Get current user's profile ID
  const { data: profile, error: pError } = await supabase
    .from("profiles")
    .select("id")
    .eq("id", user.id)
    .maybeSingle();
    
  if (pError) console.error("Erro ao buscar perfil do profissional:", pError);
    
  // FALLBACK: Use user.id if profile not found, but log it as it might fail FK
  const myProfileId = profile?.id || user.id;
  const expires_at = validityDays ? new Date(Date.now() + validityDays * 86400000).toISOString() : null;
  
  const { data, error } = await supabase
    .from("credit_ledger")
    .insert({
      patient_id: patientId,
      professional_id: myProfileId,
      clinic_id,
      amount: numAmount,
      reason,
      expires_at
    })
    .select()
    .maybeSingle();

  if (error) {
    console.error("Erro ao inserir no ledger:", error);
    throw error;
  }
  return data;
}

export async function getPatientBalance(patientId, { clinic_id, professional_id } = {}) {
  // Saldo real = soma de TODAS as entradas do paciente, sem filtros adicionais.
  // Filtrar por clinic_id ou professional_id causaria divergência de valores entre views.
  const { data, error } = await supabase
    .from("credit_ledger")
    .select("amount")
    .eq("patient_id", patientId);

  if (error) throw error;
  return (data || []).reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
}

export async function listCreditLedger(patientId, clinic_id = null) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  
  let q = supabase
    .from("credit_ledger")
    .select("*")
    .eq("patient_id", patientId);

  // Se estiver em contexto de clínica, mostra tudo daquela clínica ou do profissional direto
  if (clinic_id) {
    q = q.or(`clinic_id.eq.${clinic_id},professional_id.eq.${user.id}`);
  } else {
    // Se for paciente, vê o dele. Se for profissional, vê o que ele deu.
    q = q.or(`professional_id.eq.${user.id},patient_id.eq.${user.id}`);
  }

  const { data, error } = await q.order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

/* =====================================================================
   Professional Availability
   ===================================================================== */

export async function listAvailability(professionalId) {
  if (!professionalId) return [];
  
  const { data, error } = await supabase
    .from("professional_availability")
    .select("*")
    .eq("professional_id", professionalId)
    .order("day_of_week", { ascending: true })
    .order("start_time", { ascending: true });
    
  if (error) throw error;
  return data || [];
}

export async function upsertAvailability(payload) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const profUserId = user.id;
  const { data, error } = await supabase
    .from("professional_availability")
    .upsert({ ...payload, professional_id: profUserId })
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function deleteAvailability(id) {
  const { error } = await supabase.from("professional_availability").delete().eq("id", id);
  if (error) throw error;
}

/* =====================================================================
   Patient Booking Logic
   ===================================================================== */

export async function getAvailableSlots(professionalId, dateStr) {
  // 1. Get recurring availability for that day of week
  const date = new Date(dateStr + "T12:00:00");
  const dayOfWeek = date.getDay();
  
  const { data: avail } = await supabase
    .from("professional_availability")
    .select("*")
    .eq("professional_id", professionalId)
    .eq("day_of_week", dayOfWeek);

  if (!avail || avail.length === 0) return [];

  // 2. Get busy slots (appointments + blocks) via secure RPC (privacy-safe)
  const { data: obstacles, error: busyErr } = await supabase.rpc("get_busy_slots", {
    p_professional_id: professionalId,
    p_date_from: `${dateStr}T00:00:00Z`,
    p_date_to: `${dateStr}T23:59:59Z`
  });

  if (busyErr) {
    console.error("Erro ao buscar horários ocupados:", busyErr);
    throw busyErr;
  }

  // 3. Generate slots of 50min (or defined duration)
  let allSlots = [];
  avail.forEach(period => {
    if (!period.start_time || !period.end_time) return;

    try {
      // Use a safer parsing method
      const [sh, sm] = period.start_time.split(":").map(Number);
      const [eh, em] = period.end_time.split(":").map(Number);
      
      let current = new Date(dateStr + "T00:00:00");
      current.setHours(sh, sm, 0, 0);
      
      const end = new Date(dateStr + "T00:00:00");
      end.setHours(eh, em, 0, 0);

      const duration = Number(period.slot_duration_min) || 50;

      if (isNaN(current.getTime()) || isNaN(end.getTime()) || duration <= 0) {
        console.warn("Pulando período de disponibilidade inválido:", period);
        return;
      }

      while (current < end) {
        const sStart = current.getTime();
        const sEnd = sStart + duration * 60000;
        
        // Check for ANY overlap using timestamp math
        const isOccupied = obstacles?.some(o => {
          const oStart = new Date(o.start_at).getTime();
          const oEnd = new Date(o.end_at).getTime();
          return sStart < oEnd && sEnd > oStart;
        });

        if (!isOccupied) {
          allSlots.push({ 
            start: new Date(sStart).toISOString(), 
            end: new Date(sEnd).toISOString() 
          });
        }
        current = new Date(sEnd);
        if (current.getTime() <= sStart) break; // Prevent infinite loop
      }
    } catch (err) {
      console.error("Erro ao processar período:", period, err);
    }
  });

  return allSlots;
}

/* =====================================================================
   Professional Blocks Logic
   ===================================================================== */

export async function createBlock(payload) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const profUserId = user.id;
  const { data, error } = await supabase
    .from("professional_blocks")
    .insert({ ...payload, professional_id: profUserId })
    .select();
  if (error) throw error;
  return data;
}

export async function listBlocks() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const profUserId = user.id;
  const { data, error } = await supabase
    .from("professional_blocks")
    .select("*")
    .eq("professional_id", profUserId)
    .gte("start_at", new Date().toISOString())
    .order("start_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function deleteBlock(id) {
  const { error } = await supabase.from("professional_blocks").delete().eq("id", id);
  if (error) throw error;
}

export async function createBooking(professionalId, startAt, endAt) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Usuário não autenticado.");
  const patientId = user.id;

  // Find clinic context from link
  const { data: link } = await supabase
    .from("patient_links")
    .select("clinic_id")
    .eq("patient_id", patientId)
    .eq("professional_id", professionalId)
    .eq("status", "active")
    .maybeSingle();

  const clinicId = link?.clinic_id;

  // 1. Check Credit Balance (only if professional has credit system enabled)
  const usesCredits = await getProfessionalUsesCredits(professionalId);
  if (usesCredits) {
    const balance = await getPatientBalance(patientId, { clinic_id: clinicId, professional_id: professionalId });
    if (balance <= 0) throw new Error("Você não possui créditos suficientes. Solicite um plano ao seu profissional.");
  }

  // 2. Check Weekly Limit (Max 2 sessions)
  let startOfWeek, endOfWeek;
  try {
    const d = new Date(startAt);
    if (isNaN(d.getTime())) throw new Error("Invalid Date");
    
    const sw = new Date(d);
    sw.setDate(d.getDate() - d.getDay());
    startOfWeek = sw.toISOString().split("T")[0];
    
    const ew = new Date(sw);
    ew.setDate(sw.getDate() + 6);
    endOfWeek = ew.toISOString().split("T")[0];
  } catch (err) {
    console.error("Erro ao calcular semana:", err);
    startOfWeek = startAt.split("T")[0];
    endOfWeek = startAt.split("T")[0];
  }

  const { data: existing, error: countErr } = await supabase
    .from("appointments")
    .select("id")
    .eq("patient_id", patientId)
    .eq("professional_id", professionalId)
    .gte("start_at", `${startOfWeek}T00:00:00`)
    .lte("start_at", `${endOfWeek}T23:59:59`)
    .not("status", "eq", "cancelado");

  if (countErr) throw countErr;
  if (existing && existing.length >= 2) throw new Error("Limite de 2 sessões por semana atingido para este profissional.");

  // 3. Create Appointment
  const { data: appts, error: apptErr } = await supabase
    .from("appointments")
    .insert({
      patient_id: patientId,
      professional_id: professionalId,
      clinic_id: clinicId,
      start_at: startAt,
      end_at: endAt,
      status: "agendado",
      type: "consulta",
      modality: "online"
    })
    .select();

  if (apptErr) throw apptErr;

  // 4. Consume Credit (only if professional has credit system enabled)
  if (usesCredits) {
    const { error: ledgerErr } = await supabase.from("credit_ledger").insert({
      patient_id: patientId,
      professional_id: professionalId,
      clinic_id: clinicId,
      amount: -1,
      reason: `Agendamento: ${formatDateTime(startAt)}`,
      expires_at: null
    });
    if (ledgerErr) {
      console.error("Erro ao consumir crédito:", ledgerErr);
    }
  }

  return appts?.[0];
}

/* =====================================================================
   Clinics & Multi-tenancy
   ===================================================================== */

export async function getMyClinic() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  // Find if user is a member of any clinic
  const { data: membership, error: e1 } = await supabase
    .from("clinic_members")
    .select("clinic_id, role, clinic:clinics(*)")
    .eq("profile_id", user.id)
    .maybeSingle();

  if (e1) throw e1;
  if (!membership) return null;

  return {
    ...membership.clinic,
    my_role: membership.role
  };
}

export async function createClinic(name) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Não autenticado");

  const { data, error } = await supabase
    .from("clinics")
    .insert({ name, owner_id: user.id })
    .select()
    .maybeSingle();

  if (error) throw error;
  return data;
}



export async function updateClinic(updates) {
  const clinic = await getMyClinic();
  if (!clinic) throw new Error("Clínica não encontrada");
  if (clinic.my_role !== "admin") throw new Error("Apenas administradores podem alterar dados da clínica");

  const { data, error } = await supabase
    .from("clinics")
    .update(updates)
    .eq("id", clinic.id)
    .select()
    .maybeSingle();

  if (error) throw error;
  return data;
}
export async function getProfessionalProfile(profileId) {
  const { data, error } = await supabase
    .from("professional_profiles")
    .select("*, profile:profiles(full_name, email)")
    .eq("profile_id", profileId)
    .maybeSingle();
  if (error) throw error;
  return data; // includes professional_type, use_credits, session_price, discount_* fields
}

/**
 * Returns whether a professional has the credit system enabled.
 * Defaults to true (credits enabled) if the profile is not found.
 */
export async function getProfessionalUsesCredits(professionalId) {
  if (!professionalId) return true;
  const { data, error } = await supabase
    .from("professional_profiles")
    .select("use_credits")
    .eq("profile_id", professionalId)
    .maybeSingle();
  if (error) {
    console.warn("Erro ao verificar use_credits:", error);
    return true; // safe fallback: treat credits as enabled
  }
  return data?.use_credits ?? true;
}

export async function updateProfessionalProfile(profileId, updates) {
  const { data, error } = await supabase
    .from("professional_profiles")
    .update(updates)
    .eq("profile_id", profileId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function updateProfile(profileId, updates) {
  const { data, error } = await supabase
    .from("profiles")
    .update(updates)
    .eq("id", profileId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}
export async function listMyPlanRequests() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from("plan_requests")
    .select(`
      *,
      plan:plans(name, credits_amount),
      professional:profiles!plan_requests_professional_id_fkey(full_name)
    `)
    .eq("patient_id", user.id)
    .order("requested_at", { ascending: false });

  if (error) throw error;
  return data;
}

/* =====================================================================
   Admin Actions (Superadmin only)
   ===================================================================== */
export async function listPendingProfessionals() {
  // 1. Buscar todos os IDs que estão em clinic_members
  const { data: members } = await supabase.from("clinic_members").select("profile_id");
  const memberIds = members?.map(m => m.profile_id) || [];

  // 2. Buscar profissionais pendentes que NÃO estão na lista de membros de clínica
  let query = supabase
    .from("profiles")
    .select("id, full_name, email, system_plan_id, created_at")
    .eq("role", "professional")
    .eq("is_approved", false);

  if (memberIds.length > 0) {
    query = query.not("id", "in", `(${memberIds.join(',')})`);
  }

  const { data, error } = await query.order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function approveProfessional(profileId) {
  const { data, error } = await supabase
    .from("profiles")
    .update({ is_approved: true, subscription_status: 'active' })
    .eq("id", profileId)
    .select()
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function updateUserSubscription(profileId, updates) {
  const { data, error } = await supabase
    .from("profiles")
    .update(updates)
    .eq("id", profileId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

/* =====================================================================
   SISTEMA FINANCEIRO E GESTÃO DE REPASSES (SUPABASE & MOCK FALLBACKS)
   ===================================================================== */

const MOCK_TRANSACTIONS_KEY = "iatros_mock_financial_transactions";
const MOCK_BATCHES_KEY = "iatros_mock_financial_batches";

function getMockTransactions() {
  const data = localStorage.getItem(MOCK_TRANSACTIONS_KEY);
  if (!data) {
    const now = new Date();
    const defaultData = [
      {
        id: "tx-1",
        clinic_id: "clinic-default",
        professional_id: "prof-1",
        professional_name: "Dr. Romulo Santos",
        patient_id: "pat-1",
        patient_name: "Ana Silva",
        gross_amount: 150.00,
        payment_method: "pix",
        gateway_fee_percent: 0.00,
        gateway_fee_amount: 0.00,
        net_amount_after_fees: 150.00,
        clinic_commission_percent: 30.00,
        clinic_commission_amount: 45.00,
        professional_amount: 105.00,
        competence_date: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 2).toISOString(),
        received_at: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 2).toISOString(),
        status: "received",
        batch_id: "batch-1",
        notes: "Consulta de rotina integrada"
      },
      {
        id: "tx-2",
        clinic_id: "clinic-default",
        professional_id: "prof-1",
        professional_name: "Dr. Romulo Santos",
        patient_id: "pat-2",
        patient_name: "Carlos Eduardo",
        gross_amount: 220.00,
        payment_method: "credit_card",
        gateway_fee_percent: 3.00,
        gateway_fee_amount: 6.60,
        net_amount_after_fees: 213.40,
        clinic_commission_percent: 30.00,
        clinic_commission_amount: 66.00,
        professional_amount: 147.40,
        competence_date: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 5).toISOString(),
        received_at: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 4).toISOString(),
        status: "received",
        batch_id: "batch-1",
        notes: "Sessão de psicoterapia avançada"
      },
      {
        id: "tx-3",
        clinic_id: "clinic-default",
        professional_id: "prof-1",
        professional_name: "Dr. Romulo Santos",
        patient_id: "pat-3",
        patient_name: "Mariana Souza",
        gross_amount: 180.00,
        payment_method: "debit_card",
        gateway_fee_percent: 1.50,
        gateway_fee_amount: 2.70,
        net_amount_after_fees: 177.30,
        clinic_commission_percent: 30.00,
        clinic_commission_amount: 54.00,
        professional_amount: 123.30,
        competence_date: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).toISOString(),
        received_at: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).toISOString(),
        status: "received",
        batch_id: null,
        notes: "Consulta telemedicina de acompanhamento"
      },
      {
        id: "tx-4",
        clinic_id: "clinic-default",
        professional_id: "prof-2",
        professional_name: "Dra. Letícia Costa",
        patient_id: "pat-4",
        patient_name: "Beatriz Oliveira",
        gross_amount: 300.00,
        payment_method: "health_insurance",
        gateway_fee_percent: 10.00,
        gateway_fee_amount: 30.00,
        net_amount_after_fees: 270.00,
        clinic_commission_percent: 40.00,
        clinic_commission_amount: 120.00,
        professional_amount: 150.00,
        competence_date: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 12).toISOString(),
        received_at: null,
        status: "pending",
        batch_id: null,
        notes: "Consulta Bradesco Saúde Integrativa"
      },
      {
        id: "tx-5",
        clinic_id: "clinic-default",
        professional_id: "prof-1",
        professional_name: "Dr. Romulo Santos",
        patient_id: "pat-5",
        patient_name: "Júlio Cesar",
        gross_amount: 120.00,
        payment_method: "cash",
        gateway_fee_percent: 0.00,
        gateway_fee_amount: 0.00,
        net_amount_after_fees: 120.00,
        clinic_commission_percent: 25.00,
        clinic_commission_amount: 30.00,
        professional_amount: 90.00,
        competence_date: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 8).toISOString(),
        received_at: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 8).toISOString(),
        status: "received",
        batch_id: "batch-1",
        notes: "Avaliação inicial presencial"
      },
      {
        id: "tx-6",
        clinic_id: "clinic-default",
        professional_id: "prof-2",
        professional_name: "Dra. Letícia Costa",
        patient_id: "pat-1",
        patient_name: "Ana Silva",
        gross_amount: 250.00,
        payment_method: "credit_card",
        gateway_fee_percent: 3.00,
        gateway_fee_amount: 7.50,
        net_amount_after_fees: 242.50,
        clinic_commission_percent: 30.00,
        clinic_commission_amount: 75.00,
        professional_amount: 167.50,
        competence_date: new Date(now.getFullYear(), now.getMonth() - 1, 10).toISOString(),
        received_at: new Date(now.getFullYear(), now.getMonth() - 1, 11).toISOString(),
        status: "received",
        batch_id: "batch-2",
        notes: "Exame clínico de diagnóstico"
      },
      {
        id: "tx-7",
        clinic_id: "clinic-default",
        professional_id: "prof-2",
        professional_name: "Dra. Letícia Costa",
        patient_id: "pat-6",
        patient_name: "Fernando Lima",
        gross_amount: 200.00,
        payment_method: "pix",
        gateway_fee_percent: 0.00,
        gateway_fee_amount: 0.00,
        net_amount_after_fees: 200.00,
        clinic_commission_percent: 30.00,
        clinic_commission_amount: 60.00,
        professional_amount: 140.00,
        competence_date: new Date(now.getFullYear(), now.getMonth() - 1, 15).toISOString(),
        received_at: new Date(now.getFullYear(), now.getMonth() - 1, 15).toISOString(),
        status: "received",
        batch_id: "batch-2",
        notes: "Consulta de acompanhamento bioimpedância"
      }
    ];
    localStorage.setItem(MOCK_TRANSACTIONS_KEY, JSON.stringify(defaultData));
    return defaultData;
  }
  return JSON.parse(data);
}

function saveMockTransactions(txs) {
  localStorage.setItem(MOCK_TRANSACTIONS_KEY, JSON.stringify(txs));
}

function getMockBatches() {
  const data = localStorage.getItem(MOCK_BATCHES_KEY);
  if (!data) {
    const now = new Date();
    const defaultData = [
      {
        id: "batch-1",
        clinic_id: "clinic-default",
        professional_id: "prof-1",
        professional_name: "Dr. Romulo Santos",
        start_date: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString(),
        end_date: new Date(now.getFullYear(), now.getMonth() - 1, 30).toISOString(),
        total_gross: 490.00,
        total_fees: 6.60,
        total_clinic: 141.00,
        total_professional: 342.40,
        status: "paid",
        paid_at: new Date(now.getFullYear(), now.getMonth(), 5).toISOString(),
        notes: "Repasse mensal efetuado com sucesso"
      },
      {
        id: "batch-2",
        clinic_id: "clinic-default",
        professional_id: "prof-2",
        professional_name: "Dra. Letícia Costa",
        start_date: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString(),
        end_date: new Date(now.getFullYear(), now.getMonth() - 1, 30).toISOString(),
        total_gross: 450.00,
        total_fees: 7.50,
        total_clinic: 135.00,
        total_professional: 307.50,
        status: "pending_payment",
        paid_at: null,
        notes: "Fechamento mensal aprovado — aguardando transferência bancária"
      }
    ];
    localStorage.setItem(MOCK_BATCHES_KEY, JSON.stringify(defaultData));
    return defaultData;
  }
  return JSON.parse(data);
}

function saveMockBatches(batches) {
  localStorage.setItem(MOCK_BATCHES_KEY, JSON.stringify(batches));
}

export async function listFinancialTransactions({ clinic_id, professional_id }) {
  try {
    let q = supabase
      .from("financial_transactions")
      .select(`
        *,
        patient:profiles!financial_transactions_patient_id_fkey ( id, full_name ),
        professional:profiles!financial_transactions_professional_id_fkey ( id, full_name )
      `);

    if (clinic_id) {
      q = q.eq("clinic_id", clinic_id);
    }
    if (professional_id) {
      q = q.eq("professional_id", professional_id);
    }

    const { data, error } = await q.order("competence_date", { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.warn("[Financeiro] Usando fallback local para transações:", err.message);
    const mockTxs = getMockTransactions();
    let filtered = [...mockTxs];
    if (professional_id) {
      filtered = filtered.filter(t => t.professional_id === professional_id || t.professional_id === 'prof-1');
    }
    return filtered.map(t => ({
      ...t,
      patient: t.patient_id ? { id: t.patient_id, full_name: t.patient_name } : null,
      professional: { id: t.professional_id, full_name: t.professional_name }
    }));
  }
}

export async function createFinancialTransaction(payload) {
  try {
    const { professional_name, patient_name, ...supabasePayload } = payload;
    const { data, error } = await supabase
      .from("financial_transactions")
      .insert([supabasePayload])
      .select()
      .maybeSingle();
    if (error) throw error;
    return data;
  } catch (err) {
    console.warn("[Financeiro] Usando fallback local para criar transação:", err.message);
    const mockTxs = getMockTransactions();
    const newTx = {
      id: "tx-" + Math.random().toString(36).substr(2, 9),
      ...payload,
      patient_name: payload.patient_name || "Paciente Avulso",
      professional_name: payload.professional_name || "Dr. Romulo Santos",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    mockTxs.unshift(newTx);
    saveMockTransactions(mockTxs);
    return newTx;
  }
}

export async function updateFinancialTransaction(id, updates) {
  try {
    const { data, error } = await supabase
      .from("financial_transactions")
      .update(updates)
      .eq("id", id)
      .select()
      .maybeSingle();
    if (error) throw error;
    return data;
  } catch (err) {
    console.warn("[Financeiro] Usando fallback local para editar transação:", err.message);
    const mockTxs = getMockTransactions();
    const idx = mockTxs.findIndex(t => t.id === id);
    if (idx !== -1) {
      mockTxs[idx] = { ...mockTxs[idx], ...updates, updated_at: new Date().toISOString() };
      saveMockTransactions(mockTxs);
      return mockTxs[idx];
    }
    return null;
  }
}

export async function deleteFinancialTransaction(id) {
  try {
    const { error } = await supabase
      .from("financial_transactions")
      .delete()
      .eq("id", id);
    if (error) throw error;
    return true;
  } catch (err) {
    console.warn("[Financeiro] Usando fallback local para deletar transação:", err.message);
    const mockTxs = getMockTransactions();
    const filtered = mockTxs.filter(t => t.id !== id);
    saveMockTransactions(filtered);
    return true;
  }
}

export async function listFinancialBatches({ clinic_id, professional_id }) {
  try {
    let q = supabase
      .from("financial_batches")
      .select(`
        *,
        professional:profiles!financial_batches_professional_id_fkey ( id, full_name )
      `);

    if (clinic_id) {
      q = q.eq("clinic_id", clinic_id);
    }
    if (professional_id) {
      q = q.eq("professional_id", professional_id);
    }

    const { data, error } = await q.order("created_at", { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.warn("[Financeiro] Usando fallback local para lotes de repasses:", err.message);
    const mockBatches = getMockBatches();
    let filtered = [...mockBatches];
    if (professional_id) {
      filtered = filtered.filter(b => b.professional_id === professional_id || b.professional_id === 'prof-1');
    }
    return filtered.map(b => ({
      ...b,
      professional: { id: b.professional_id, full_name: b.professional_name }
    }));
  }
}

export async function createFinancialBatch(payload) {
  try {
    const { data, error } = await supabase
      .from("financial_batches")
      .insert([payload])
      .select()
      .maybeSingle();
    if (error) throw error;
    
    // Atualizar as transações incluídas para apontarem para o batch_id gerado
    if (data && payload.transaction_ids?.length > 0) {
      await supabase
        .from("financial_transactions")
        .update({ batch_id: data.id })
        .in("id", payload.transaction_ids);
    }
    return data;
  } catch (err) {
    console.warn("[Financeiro] Usando fallback local para gerar lote de repasse:", err.message);
    const mockBatches = getMockBatches();
    const batchId = "batch-" + Math.random().toString(36).substr(2, 9);
    
    const newBatch = {
      id: batchId,
      clinic_id: payload.clinic_id || "clinic-default",
      professional_id: payload.professional_id,
      professional_name: payload.professional_name || "Profissional Parceiro",
      start_date: payload.start_date,
      end_date: payload.end_date,
      total_gross: payload.total_gross,
      total_fees: payload.total_fees,
      total_clinic: payload.total_clinic,
      total_professional: payload.total_professional,
      status: "pending_payment",
      paid_at: null,
      notes: payload.notes || "Lote de repasse consolidado no fechamento",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    mockBatches.unshift(newBatch);
    saveMockBatches(mockBatches);

    // Marcar as transações no localStorage como associadas a este lote
    if (payload.transaction_ids && payload.transaction_ids.length > 0) {
      const mockTxs = getMockTransactions();
      payload.transaction_ids.forEach(txId => {
        const tx = mockTxs.find(t => t.id === txId);
        if (tx) {
          tx.batch_id = batchId;
        }
      });
      saveMockTransactions(mockTxs);
    }

    return newBatch;
  }
}

export async function payFinancialBatch(batchId) {
  try {
    const { data, error } = await supabase
      .from("financial_batches")
      .update({ status: "paid", paid_at: new Date().toISOString() })
      .eq("id", batchId)
      .select()
      .maybeSingle();
    if (error) throw error;
    return data;
  } catch (err) {
    console.warn("[Financeiro] Usando fallback local para efetuar pagamento do lote:", err.message);
    const mockBatches = getMockBatches();
    const idx = mockBatches.findIndex(b => b.id === batchId);
    if (idx !== -1) {
      mockBatches[idx].status = "paid";
      mockBatches[idx].paid_at = new Date().toISOString();
      mockBatches[idx].updated_at = new Date().toISOString();
      saveMockBatches(mockBatches);
      return mockBatches[idx];
    }
    return null;
  }
}

